import { type EIP1193Provider } from 'viem'
import { appChain } from '@/lib/chain'

const toHexChainId = (id: number): `0x${string}` => `0x${id.toString(16)}` as `0x${string}`

/** Some wallets report Base (8453) as `0x8453` instead of the correct `0x2105`. */
const misreportedDecimalAsHex = (): string => `0x${appChain.id}`

const appRpcUrl = (): string =>
  appChain.id === 84532
    ? (import.meta.env.VITE_BASE_SEPOLIA_RPC_URL ?? 'https://sepolia.base.org')
    : (import.meta.env.VITE_BASE_RPC_URL ?? 'https://mainnet.base.org')

const asProvider = (provider: unknown): EIP1193Provider => provider as EIP1193Provider

const isOnAppChain = (chainId: number): boolean => {
  if (chainId === appChain.id) return true
  // Parsed misreport: eth_chainId "0x8453" → 33875 while appChain.id is 8453 (Base).
  if (chainId === Number.parseInt(misreportedDecimalAsHex(), 16)) return true
  return false
}

const normalizeReportedChainId = (raw: string): number => {
  const trimmed = String(raw).trim()
  if (/^\d+$/.test(trimmed)) return Number.parseInt(trimmed, 10)

  const hex = trimmed.toLowerCase()
  const expected = toHexChainId(appChain.id).toLowerCase()
  const misreported = misreportedDecimalAsHex().toLowerCase()
  if (hex === expected || hex === misreported) return appChain.id
  return Number.parseInt(hex, 16)
}

const coerceChainId = (raw: unknown): number => {
  if (raw == null) {
    throw new Error('Wallet returned an unreadable chain id.')
  }

  if (typeof raw === 'object' && 'chainId' in raw) {
    return coerceChainId((raw as { chainId: unknown }).chainId)
  }

  let id: number
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    id = Math.trunc(raw)
  } else if (typeof raw === 'bigint') {
    id = Number(raw)
  } else if (typeof raw === 'string' || raw instanceof String) {
    id = normalizeReportedChainId(String(raw))
  } else {
    throw new Error('Wallet returned an unreadable chain id.')
  }

  if (isOnAppChain(id)) return appChain.id
  return id
}

const readChainId = async (provider: EIP1193Provider): Promise<number> => {
  const raw = await provider.request({ method: 'eth_chainId' })
  return coerceChainId(raw)
}

/**
 * Wallets sometimes return eth_chainId as a number. Viem expects a hex string and
 * calls `.toLowerCase()` on the value — wrap the provider so chain id is always hex.
 */
export const wrapEip1193ChainId = (provider: unknown): EIP1193Provider => {
  const p = asProvider(provider)
  const request = p.request.bind(p) as (args: {
    method: string
    params?: unknown
  }) => Promise<unknown>

  return {
    ...p,
    request: (async (args) => {
      const result = await request(args as { method: string; params?: unknown })
      if (args.method === 'eth_chainId') {
        return toHexChainId(coerceChainId(result))
      }
      return result
    }) as EIP1193Provider['request'],
  }
}

const addAppChain = async (provider: EIP1193Provider) => {
  const explorer = appChain.blockExplorers?.default?.url
  await provider.request({
    method: 'wallet_addEthereumChain',
    params: [
      {
        chainId: toHexChainId(appChain.id),
        chainName: appChain.name,
        nativeCurrency: appChain.nativeCurrency,
        rpcUrls: [appRpcUrl()],
        ...(explorer ? { blockExplorerUrls: [explorer] } : {}),
      },
    ],
  })
}

/**
 * Switch external wallets to the app chain, then return a provider safe for viem.
 */
export const prepareEip1193Provider = async (provider: unknown): Promise<EIP1193Provider> => {
  await ensureWalletOnAppChain(provider)
  return wrapEip1193ChainId(provider)
}

/**
 * Prompt the connected wallet to switch to the app's configured chain (Base / Base Sepolia)
 * before sending contract transactions via EIP-1193.
 */
export const ensureWalletOnAppChain = async (provider: unknown): Promise<void> => {
  const p = asProvider(provider)
  const current = await readChainId(p)
  if (isOnAppChain(current)) return

  try {
    await p.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: toHexChainId(appChain.id) }],
    })
  } catch (e) {
    const code = (e as { code?: number })?.code
    if (code === 4902) {
      await addAppChain(p)
      return
    }
    if (code === 4001) {
      throw new Error(`Switch to ${appChain.name} in your wallet to mint on Memoria.`)
    }
    throw new Error(
      `Your wallet is on chain ${current}, but Memoria mints on ${appChain.name} (chain ${appChain.id}). Switch networks in your wallet and try again.`
    )
  }

  const after = await readChainId(p)
  if (!isOnAppChain(after)) {
    throw new Error(
      `Your wallet is still on chain ${after}. Switch to ${appChain.name} (chain ${appChain.id}) and try again.`
    )
  }
}
